import Color from '@/Enums/Color.enum';
import DefaultRangeTime from '@/Enums/DateTime/DefaultRangeTime.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { AccessTime } from '@mui/icons-material';
import { Box, Button, IconButton, OutlinedInput, Stack, Tooltip } from '@mui/material';
import { MobileTimePicker, PickersActionBarProps } from '@mui/x-date-pickers';
import { DateRange } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import { useCallback, useMemo, useState } from 'react';

const getDisplayedValue = (value?: Moment | string | null): string =>
  value ? moment(value).format(DateFormat.NumericMonthDayYear) : '';

interface IProps extends PickersActionBarProps {
  value?: DateRange<Moment>;
  className?: string;
  onRangeChange?: (next: DateRange<Moment>) => void;
  onNestedPickerOpenChange?: (open: boolean) => void;
}

const ActionBarWithTimeHandling = ({
  onAccept,
  onCancel,
  value,
  className,
  onRangeChange,
  onNestedPickerOpenChange,
}: IProps) => {
  const [openStart, setOpenStart] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);

  const handleOpenStart = useCallback(
    (open: boolean) => {
      setOpenStart(open);
      onNestedPickerOpenChange?.(open);
    },
    [onNestedPickerOpenChange],
  );

  const handleOpenEnd = useCallback(
    (open: boolean) => {
      setOpenEnd(open);
      onNestedPickerOpenChange?.(open);
    },
    [onNestedPickerOpenChange],
  );

  const startValue = value?.[0] ?? null;
  const endValue = value?.[1] ?? null;

  const startTimeCustomized = useMemo(() => {
    if (!startValue) return false;
    return !(
      startValue.hours() === DefaultRangeTime.StartHour &&
      startValue.minutes() === DefaultRangeTime.StartMin &&
      startValue.seconds() === DefaultRangeTime.StartSec
    );
  }, [startValue]);

  const endTimeCustomized = useMemo(() => {
    if (!endValue) return false;
    return !(
      endValue.hours() === DefaultRangeTime.EndHour &&
      endValue.minutes() === DefaultRangeTime.EndMin &&
      endValue.seconds() === DefaultRangeTime.EndSec
    );
  }, [endValue]);

  const handleStartTimeChange = useCallback(
    (newTime: Moment | null) => {
      if (!newTime || !startValue) return;
      const updated = startValue
        .clone()
        .hours(newTime.hours())
        .minutes(newTime.minutes())
        .seconds(DefaultRangeTime.StartSec)
        .milliseconds(DefaultRangeTime.StartMs);
      onRangeChange?.([updated, endValue]);
    },
    [endValue, onRangeChange, startValue],
  );

  const handleEndTimeChange = useCallback(
    (newTime: Moment | null) => {
      if (!newTime || !endValue) return;
      const updated = endValue
        .clone()
        .hours(newTime.hours())
        .minutes(newTime.minutes())
        .seconds(DefaultRangeTime.EndSec)
        .milliseconds(DefaultRangeTime.EndMs);
      onRangeChange?.([startValue, updated]);
    },
    [endValue, onRangeChange, startValue],
  );

  const startPlaceholder = useMemo(() => DateFormat.NumericMonthDayYear, []);
  const endPlaceholder = useMemo(() => DateFormat.NumericMonthDayYear, []);

  const startTimeTitle = useMemo(() => {
    return startValue && startValue.format('hh:mm A');
  }, [startValue]);
  const endTimeTitle = useMemo(() => {
    return endValue && endValue.format('hh:mm A');
  }, [endValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopWidth: '1px',
        borderTopColor: Color.GreyBorder,
        padding: '16px',
      }}
      className={className}
    >
      <Stack
        spacing='16px'
        direction='row'
        divider={<p className='leading-[2.7]'>—</p>}
        sx={{
          width: 350,
          height: 40,
        }}
      >
        <Stack
          direction='row'
          spacing='8px'
          sx={{
            alignItems: 'center',
            height: 40,
          }}
        >
          <OutlinedInput readOnly value={getDisplayedValue(startValue)} placeholder={startPlaceholder} />
          <Tooltip title={startTimeTitle} placement='top'>
            <IconButton
              aria-label='Set start time'
              size='small'
              onClick={e => {
                e.stopPropagation();
                handleOpenStart(true);
              }}
              color={startTimeCustomized ? 'primary' : 'default'}
            >
              <AccessTime
                fontSize='small'
                sx={[
                  startTimeCustomized
                    ? {
                        color: null,
                      }
                    : {
                        color: 'text.disabled',
                      },
                ]}
              />
            </IconButton>
          </Tooltip>
          <MobileTimePicker
            ampm
            open={openStart}
            onClose={() => handleOpenStart(false)}
            value={startValue}
            onChange={handleStartTimeChange}
            views={['hours', 'minutes']}
            slotProps={{
              textField: { style: { display: 'none' } },
            }}
          />
        </Stack>
        <Stack
          direction='row'
          spacing='8px'
          sx={{
            alignItems: 'center',
            height: 40,
          }}
        >
          <OutlinedInput readOnly value={getDisplayedValue(endValue)} placeholder={endPlaceholder} />
          <Tooltip disableInteractive={!endTimeTitle} title={endTimeTitle} placement='top'>
            <IconButton
              aria-label='Set end time'
              size='small'
              onClick={e => {
                e.stopPropagation();
                handleOpenEnd(true);
              }}
              color={endTimeCustomized ? 'primary' : 'default'}
            >
              <AccessTime
                fontSize='small'
                sx={[
                  endTimeCustomized
                    ? {
                        color: null,
                      }
                    : {
                        color: 'text.disabled',
                      },
                ]}
              />
            </IconButton>
          </Tooltip>
          <MobileTimePicker
            ampm
            open={openEnd}
            onClose={() => handleOpenEnd(false)}
            value={endValue}
            onChange={handleEndTimeChange}
            views={['hours', 'minutes']}
            slotProps={{
              textField: { style: { display: 'none' } },
            }}
          />
        </Stack>
      </Stack>
      <Stack
        spacing='8px'
        direction='row'
        sx={{
          height: 40,
        }}
      >
        <Button variant='outlined' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='contained' onClick={onAccept} disabled={value?.some(v => !v)}>
          Apply
        </Button>
      </Stack>
    </Box>
  );
};
export default ActionBarWithTimeHandling;
